import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  effect,
  OnInit,
  ViewChild,
  afterNextRender,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/authentication/auth.service';
import {
  CONTEXT_RETURN_URL_QUERY_PARAM,
  normalizeContextReturnUrl,
} from '../../core/platform/context-return-url';
import { PendingChangesService } from '../../core/navigation/pending-changes.service';
import { PlatformContextService } from '../../core/platform/platform-context.service';
import { ChangePasswordDialog } from '../../features/authentication/change-password/change-password-dialog';
import { PRIMARY_NAVIGATION } from '../navigation/navigation.config';
import { BlockingErrorState } from '../../shared/feedback/blocking-error-state/blocking-error-state';
import { LoadingState } from '../../shared/feedback/loading-state/loading-state';
import { AppNavigation } from '../navigation/navigation';

const TEMPORARY_NAVIGATION_QUERY = '(max-width: 64rem)';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppNavigation,
    BlockingErrorState,
    LoadingState,
    MatButtonModule,
    MatDialogModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    RouterLink,
    RouterOutlet,
  ],
  selector: 'app-app-shell',
  styleUrl: './app-shell.scss',
  templateUrl: './app-shell.html',
})
export class AppShell implements OnInit {
  protected readonly fluidCapture = signal(false);
  private readonly authService = inject(AuthService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);
  private readonly pendingChanges = inject(PendingChangesService);
  private readonly router = inject(Router);

  @ViewChild('navigationToggle', { read: ElementRef })
  private navigationToggle?: ElementRef<HTMLButtonElement>;

  @ViewChild('mainContent', { read: ElementRef })
  private mainContent?: ElementRef<HTMLElement>;

  private restoreNavigationTriggerAfterClose = true;

  protected readonly platformContext = inject(PlatformContextService);
  private readonly failedCompanyLogoUrl = signal<string | null>(null);
  private previousCompanyLogoUrl: string | null | undefined;
  protected readonly environmentName = environment.name;
  protected readonly authenticationState = this.authService.state;
  protected readonly signingOut = signal(false);
  protected readonly retryingPreparation = signal(false);
  protected readonly primaryNavigation = PRIMARY_NAVIGATION;
  protected readonly temporaryNavigation = signal(
    this.breakpointObserver.isMatched(TEMPORARY_NAVIGATION_QUERY),
  );
  protected readonly navigationOpened = signal(!this.temporaryNavigation());
  protected readonly navigationCollapsed = signal(false);
  protected readonly companyLogo = computed(() => {
    const company = this.platformContext.selectedCompany();
    if (
      company?.logoUrl === null ||
      company?.logoUrl === undefined ||
      company.logoUrl === this.failedCompanyLogoUrl()
    ) {
      return null;
    }

    return {
      url: company.logoUrl,
      alt: `Logo de ${company.name}`,
    };
  });

  constructor() {
    this.fluidCapture.set(/^\/payroll\/periods\/[1-9]\d*\/destinations(?:\?|$)/.test(this.router.url));
    effect(() => {
      const logoUrl = this.platformContext.selectedCompany()?.logoUrl ?? null;
      if (logoUrl !== this.previousCompanyLogoUrl) {
        this.previousCompanyLogoUrl = logoUrl;
        this.failedCompanyLogoUrl.set(null);
      }
    });

    this.breakpointObserver
      .observe(TEMPORARY_NAVIGATION_QUERY)
      .pipe(takeUntilDestroyed())
      .subscribe(({ matches }) => {
        this.temporaryNavigation.set(matches);
        this.navigationOpened.set(!matches);
        if (matches) {
          this.navigationCollapsed.set(false);
        }
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.fluidCapture.set(/^\/payroll\/periods\/[1-9]\d*\/destinations(?:\?|$)/.test(this.router.url));
        this.scheduleRouteFocus();
      });
  }

  ngOnInit(): void {
    void this.authService.isAuthenticated().catch(() => undefined);
  }

  protected isContextSelectionRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return path === '/select-tenant' || path === '/select-context';
  }
  protected isRouteContentVisible(): boolean {
    return (
      this.platformContext.applicationState() === 'ready' ||
      (this.platformContext.applicationState() === 'needsIntervention' &&
        this.isContextSelectionRoute())
    );
  }

  protected async retryApplicationPreparation(): Promise<void> {
    if (this.retryingPreparation()) {
      return;
    }

    this.retryingPreparation.set(true);
    const currentTree = this.router.parseUrl(this.router.url);
    const requestedReturnUrl = currentTree.queryParams[CONTEXT_RETURN_URL_QUERY_PARAM];
    const destination = normalizeContextReturnUrl(
      this.platformContext.requestedApplicationUrl() !== '/'
        ? this.platformContext.requestedApplicationUrl()
        : typeof requestedReturnUrl === 'string'
          ? requestedReturnUrl
          : this.router.url,
    );
    this.platformContext.clear();

    try {
      const navigated = await this.router.navigateByUrl(destination, {
        onSameUrlNavigation: 'reload',
      });
      if (!navigated && this.platformContext.applicationState() === 'preparing') {
        this.platformContext.markApplicationError();
      }
    } catch {
      this.platformContext.markApplicationError();
    } finally {
      this.retryingPreparation.set(false);
    }
  }
  protected navigationOpenedChanged(opened: boolean): void {
    if (this.platformContext.applicationState() === 'ready') {
      this.navigationOpened.set(opened);
    }
  }
  protected toggleNavigation(): void {
    this.restoreNavigationTriggerAfterClose = true;
    this.navigationOpened.update((opened) => !opened);
  }

  protected navigationItemSelected(): void {
    if (this.temporaryNavigation()) {
      this.restoreNavigationTriggerAfterClose = false;
      this.navigationOpened.set(false);
    }
  }

  protected restoreNavigationFocus(): void {
    if (!this.temporaryNavigation()) {
      return;
    }

    const shouldRestore = this.restoreNavigationTriggerAfterClose;
    this.restoreNavigationTriggerAfterClose = true;
    if (shouldRestore) {
      queueMicrotask(() => this.navigationToggle?.nativeElement.focus());
    }
  }

  protected skipToMainContent(event: Event): void {
    event.preventDefault();
    this.focusElement(this.mainContent?.nativeElement);
  }

  private scheduleRouteFocus(): void {
    afterNextRender(
      () => {
        if (
          this.authenticationState() !== 'authenticated' ||
          !this.isRouteContentVisible() ||
          this.dialog.openDialogs.length > 0
        ) {
          return;
        }

        const main = this.mainContent?.nativeElement;
        const heading = main?.querySelector<HTMLElement>('h1');
        this.focusElement(heading ?? main);
      },
      { injector: this.injector },
    );
  }

  private focusElement(element: HTMLElement | undefined): void {
    if (element === undefined) {
      return;
    }

    const hadTabindex = element.hasAttribute('tabindex');
    if (!hadTabindex) {
      element.setAttribute('tabindex', '-1');
      element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
    }
    element.focus();
  }

  protected openChangePassword(): void {
    this.dialog.open(ChangePasswordDialog, {
      autoFocus: 'first-tabbable',
      maxWidth: 'calc(100vw - 2rem)',
      restoreFocus: true,
    });
  }

  protected companyLogoLoadFailed(url: string): void {
    if (this.platformContext.selectedCompany()?.logoUrl === url) {
      this.failedCompanyLogoUrl.set(url);
    }
  }

  protected async changeTenant(): Promise<void> {
    if (!await firstValueFrom(this.pendingChanges.confirmDiscard())) return;
    this.platformContext.beginTenantSelection();
    await this.navigateToContextSelection('/select-tenant');
  }

  protected async changeCompany(): Promise<void> {
    if (!await firstValueFrom(this.pendingChanges.confirmDiscard())) return;
    this.platformContext.beginCompanySelection();
    await this.navigateToContextSelection('/select-context');
  }

  protected async changeBranch(): Promise<void> {
    if (!await firstValueFrom(this.pendingChanges.confirmDiscard())) return;
    this.platformContext.beginBranchSelection();
    await this.navigateToContextSelection('/select-context');
  }

  private navigateToContextSelection(path: '/select-tenant' | '/select-context'): Promise<boolean> {
    return this.router.navigate([path], {
      queryParams: {
        [CONTEXT_RETURN_URL_QUERY_PARAM]: normalizeContextReturnUrl(this.router.url),
      },
    });
  }

  protected async signOut(): Promise<void> {
    if (this.signingOut()) {
      return;
    }

    this.signingOut.set(true);
    try {
      await this.authService.signOut();
    } catch {
      // AuthService clears local state even if remote sign-out fails.
    } finally {
      this.signingOut.set(false);
      await this.router.navigate(['/login']);
    }
  }
}
